import { Search as SearchIcon } from '@mui/icons-material';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  InputAdornment,
  Checkbox,
  Radio,
  Typography,
  Chip,
  Tabs,
  Tab,
} from '@mui/material';
import { DataGridProps, GridSortModel } from '@mui/x-data-grid';
import { Cell, PaginatedDataGrid, AdvancedFilterModel } from '@nohara-dxdev/react-core';
import { useState, useMemo, useCallback, useEffect } from 'react';
import {
  useController,
  FieldPath,
  FieldValues,
  RegisterOptions,
  useFormContext,
} from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { ItemTemplateEntityWithSchema } from '@/types/api';

import { useItemTemplateSchemas } from '../api/get-item-template-schemas';
import {
  itemTemplateSortableFields,
  ItemTemplateSortableField,
  useItemTemplateFilterValues,
} from '../providers/item-template-filter-values-provider';

import { ItemTemplateDetail } from './item-template-detail';
import { ItemTemplateSchemaDetail } from './item-template-schema-detail';

interface SelectItemTemplateModalProps<
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
> {
  name: TName;
  rules?: RegisterOptions<TFieldValues, TName>;
  open: boolean;
  onClose: () => void;
  multiValue?: boolean;
  title?: string;
}

type SelectItemTemplateModalComponent = <
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
>(
  props: SelectItemTemplateModalProps<TFieldValues, TName>,
) => React.JSX.Element;

export const SelectItemTemplateModal: SelectItemTemplateModalComponent = <
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
>({
  name,
  rules,
  open,
  onClose,
  multiValue = false,
  title,
}: SelectItemTemplateModalProps<TFieldValues, TName>) => {
  const { t } = useTranslation();
  const form = useFormContext<TFieldValues>();

  // Form integration using react-hook-form
  const {
    field: { value, onChange },
    fieldState: { error: fieldError },
    formState: { disabled },
  } = useController({
    name,
    control: form.control,
    rules,
  });

  // Get filter values from context
  const filterValues = useItemTemplateFilterValues();

  // Local state for search and selection
  const [searchValue, setSearchValue] = useState('');
  const [selectedTemplates, setSelectedTemplates] = useState<ItemTemplateEntityWithSchema[]>([]);
  // Id of the row whose schema is shown in the right preview pane (last-clicked row).
  const [previewTemplateId, setPreviewTemplateId] = useState<string | null>(null);
  // Active tab in the right pane: 0 = live preview, 1 = schema detail.
  const [previewTab, setPreviewTab] = useState(0);

  // Server-side sorting state
  const [sortField, setSortField] = useState<ItemTemplateSortableField | undefined>(undefined);
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc' | undefined>(undefined);

  // Advanced filter state (for filter panel)
  const [pendingAdvancedFilterModel, setPendingAdvancedFilterModel] = useState<AdvancedFilterModel>(
    {
      conditions: [],
    },
  );
  const [appliedAdvancedFilterModel, setAppliedAdvancedFilterModel] = useState<AdvancedFilterModel>(
    {
      conditions: [],
    },
  );

  useEffect(() => {
    if (!value) {
      setSelectedTemplates([]);
      setPreviewTemplateId(null);
      return;
    }

    if (multiValue) {
      const next = (Array.isArray(value) ? value : [value]) as ItemTemplateEntityWithSchema[];
      setSelectedTemplates(next);
      setPreviewTemplateId(next[0]?.id ?? null);
    } else {
      const single = value as unknown as ItemTemplateEntityWithSchema;
      setSelectedTemplates([single]);
      setPreviewTemplateId(single.id);
    }
  }, [value, multiValue]);

  // Build advancedFilterModel from context filter values if they exist
  // Note: The context should already provide advancedFilterModel, but we merge with any existing filters
  const combinedAdvancedFilterModel = useMemo<AdvancedFilterModel | undefined>(() => {
    // Use applied filter model if it exists (from filter panel)
    if (appliedAdvancedFilterModel.conditions.length > 0) {
      return appliedAdvancedFilterModel;
    }
    // If context already provides advancedFilterModel, use it
    if (
      filterValues.advancedFilterModel &&
      filterValues.advancedFilterModel.conditions.length > 0
    ) {
      return filterValues.advancedFilterModel;
    }
    // If context provides filterModel, convert it (though this shouldn't happen in new code)
    if (filterValues.filterModel && filterValues.filterModel.length > 0) {
      // Convert filterModel to advancedFilterModel format
      const conditions = filterValues.filterModel.flatMap((model, modelIndex) =>
        model.items.map((item, itemIndex) => ({
          item: {
            field: item.field,
            operator: item.operator,
            value: item.value ?? '',
          },
          logicOperator:
            modelIndex === 0 && itemIndex === 0
              ? undefined
              : ((model.logicOperator ?? 'and') as 'and' | 'or'),
        })),
      );
      return conditions.length > 0 ? { conditions } : undefined;
    }
    return undefined;
  }, [filterValues.advancedFilterModel, filterValues.filterModel, appliedAdvancedFilterModel]);

  // Combine search value with context filter values
  const effectiveNameOrTemplateKeyMatch = searchValue || filterValues.nameOrTemplateKeyMatch;

  // Convert state to MUI DataGrid sortModel format
  const sortModel = useMemo<GridSortModel>(() => {
    if (!sortField) {
      return [];
    }
    return [{ field: sortField, sort: sortOrder }];
  }, [sortField, sortOrder]);

  // Handle sort model changes from DataGrid
  const handleSortModelChange = useCallback((model: GridSortModel) => {
    if (model.length === 0) {
      // Reset to undefined when sorting is cleared
      setSortField(undefined);
      setSortOrder(undefined);
      return;
    }

    const { field, sort } = model[0];

    // Only allow sorting on fields that exist in the backend
    if (itemTemplateSortableFields.includes(field as ItemTemplateSortableField)) {
      setSortField(field as ItemTemplateSortableField);
      setSortOrder(sort ?? 'asc');
    }
  }, []);

  // Load item templates with advanced filter model and search
  const itemTemplatesQuery = useItemTemplateSchemas({
    nameOrTemplateKeyMatch: effectiveNameOrTemplateKeyMatch,
    advancedFilterModel: combinedAdvancedFilterModel,
    sortField,
    sortOrder,
    queryConfig: {
      enabled: open, // Only fetch when modal is open
    },
  });

  const { isLoading } = itemTemplatesQuery;

  // Handle search input change
  const handleSearchChange = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchValue(event.target.value);
  }, []);

  // Handle template selection
  const handleTemplateSelect = useCallback(
    (template: ItemTemplateEntityWithSchema) => {
      // The clicked row always becomes the preview target, even when toggling off in multi mode.
      setPreviewTemplateId(template.id);
      if (multiValue) {
        setSelectedTemplates((prev) => {
          const isSelected = prev.some((selectedTemplate) => selectedTemplate.id === template.id);
          if (isSelected) {
            return prev.filter((selectedTemplate) => selectedTemplate.id !== template.id);
          } else {
            return [...prev, template];
          }
        });
      } else {
        setSelectedTemplates([template]);
      }
    },
    [multiValue],
  );

  // Handle select button click
  const handleSelect = useCallback(() => {
    if (multiValue) {
      onChange(selectedTemplates);
    } else {
      onChange(selectedTemplates[0] || null);
    }
    onClose();
  }, [selectedTemplates, multiValue, onChange, onClose]);

  // Handle cancel
  const handleCancel = useCallback(() => {
    // Reset selection to current form value
    if (!value) {
      setSelectedTemplates([]);
      setPreviewTemplateId(null);
    } else {
      // Handle both single value and multi-value cases
      if (multiValue) {
        const next = Array.isArray(value) ? value : [value as ItemTemplateEntityWithSchema];
        setSelectedTemplates(next);
        setPreviewTemplateId(next[0]?.id ?? null);
      } else {
        const single = value as unknown as ItemTemplateEntityWithSchema;
        setSelectedTemplates([single]);
        setPreviewTemplateId(single.id);
      }
    }
    setSearchValue('');
    // Reset filters to initial state
    const emptyFilterModel: AdvancedFilterModel = { conditions: [] };
    setPendingAdvancedFilterModel(emptyFilterModel);
    setAppliedAdvancedFilterModel(emptyFilterModel);
    onClose();
  }, [value, multiValue, onClose]);

  // Handle advanced filter model changes from filter panel (only updates pending filter, not applied)
  const handleAdvancedFilterModelChange = useCallback((model: AdvancedFilterModel) => {
    setPendingAdvancedFilterModel(model);
  }, []);

  // Apply the pending advanced filter model (called from filter panel)
  const handleAdvancedFilterApply = useCallback((model: AdvancedFilterModel) => {
    setAppliedAdvancedFilterModel(model);
    setPendingAdvancedFilterModel(model);
  }, []);

  // Clear all filters (called from filter panel)
  const handleAdvancedFilterClear = useCallback(() => {
    const emptyFilterModel: AdvancedFilterModel = { conditions: [] };
    setPendingAdvancedFilterModel(emptyFilterModel);
    setAppliedAdvancedFilterModel(emptyFilterModel);
  }, []);

  // Define table columns
  const columns: DataGridProps<ItemTemplateEntityWithSchema>['columns'] = useMemo(
    () => [
      {
        field: 'selection',
        headerName: '',
        width: 80,
        sortable: false,
        filterable: false,
        renderCell: (params) => {
          const isSelected = selectedTemplates.some((template) => template.id === params.row.id);

          if (multiValue) {
            return (
              <Checkbox
                checked={isSelected}
                onChange={() => {
                  handleTemplateSelect(params.row);
                }}
                onClick={(e) => {
                  e.stopPropagation();
                }}
                onMouseDown={(e) => {
                  e.stopPropagation();
                }}
                disabled={disabled}
              />
            );
          } else {
            return (
              <Radio
                checked={isSelected}
                onChange={() => {
                  handleTemplateSelect(params.row);
                }}
                onClick={(e) => {
                  e.stopPropagation();
                }}
                onMouseDown={(e) => {
                  e.stopPropagation();
                }}
                disabled={disabled}
              />
            );
          }
        },
      },
      {
        field: 'name',
        headerName: t('items.template.table.name'),
        flex: 1,
        minWidth: 200,
        sortable: true,
      },
      {
        field: 'templateKey',
        headerName: t('items.template.table.templateKey'),
        width: 150,
        sortable: true,
      },
      {
        field: 'constructionTypes',
        headerName: t('items.template.table.constructionTypes'),
        flex: 1,
        minWidth: 250,
        filterable: false,
        renderCell: (params) => {
          const types = params.row.constructionTypes ?? [];
          if (types.length === 0) return '-';
          return (
            <Cell sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', alignItems: 'center' }}>
              {types.slice(0, 2).map((type, idx) => (
                <Chip key={`type-${idx}`} label={type.value} size="small" />
              ))}
              {types.length > 2 && <Chip label={`+${types.length - 2}`} size="small" />}
            </Cell>
          );
        },
      },
      {
        field: 'itemType',
        headerName: t('items.template.table.itemType'),
        width: 150,
        filterable: false,
        renderCell: (params) => params.row.itemType?.value ?? '-',
      },
    ],
    [selectedTemplates, multiValue, handleTemplateSelect, disabled, t],
  );

  const modalTitle =
    title ??
    (multiValue
      ? t('common.selectModal.titleMultiple', { type: t('items.template.title') })
      : t('common.selectModal.titleSingle', { type: t('items.template.title') }));

  return (
    <Dialog open={open} onClose={handleCancel} fullScreen>
      <DialogTitle>
        <Typography variant="h6">{modalTitle}</Typography>
      </DialogTitle>

      {/* overflow:hidden makes DialogContent a fixed-height flex box so each pane
          owns its own scroll instead of the whole content scrolling as one. */}
      <DialogContent sx={{ display: 'flex', p: 0, flex: 1, minHeight: 0, overflow: 'hidden' }}>
        {/* Left pane — search + table + selection summary */}
        <Box
          sx={{
            flex: previewTemplateId ? 1.22 : 1,
            display: 'flex',
            flexDirection: 'column',
            minWidth: 0,
            minHeight: 0,
            gap: 2,
            p: 3,
            ...(previewTemplateId && {
              borderRight: 1,
              borderColor: 'divider',
            }),
          }}
        >
          {/* Search Bar */}
          <TextField
            fullWidth
            placeholder={t('items.template.selectModal.searchPlaceholder')}
            value={searchValue}
            onChange={handleSearchChange}
            slotProps={{
              input: {
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
              },
            }}
            disabled={disabled}
          />

          {/* Templates Table — bounded flex wrapper. PaginatedDataGrid's own root Box is
              auto-height, so we stretch it (and the DataGrid inside) to fill this box; the
              DataGrid's virtual scroller then scrolls rows internally instead of the dialog. */}
          <Box
            sx={{
              flex: 1,
              minHeight: '100%',
              display: 'flex',
              flexDirection: 'column',
              '& > .MuiBox-root': {
                flex: 1,
                minHeight: '100%',
                display: 'flex',
                flexDirection: 'column',
              },
              '& .MuiDataGrid-root': {
                flex: 1,
                minHeight: '100%',
              },
            }}
          >
            <PaginatedDataGrid
              paginatedQuery={itemTemplatesQuery}
              columns={columns}
              showTopPagination
              loading={isLoading}
              getRowId={(row) => row.id}
              hideFooterSelectedRowCount
              pagination
              enableAdvancedFilters={true}
              advancedFilterModel={pendingAdvancedFilterModel}
              onAdvancedFilterModelChange={handleAdvancedFilterModelChange}
              onAdvancedFilterApply={handleAdvancedFilterApply}
              onAdvancedFilterClear={handleAdvancedFilterClear}
              sortModel={sortModel}
              onSortModelChange={handleSortModelChange}
              sortingMode="server"
              sx={{
                '& .MuiDataGrid-row': {
                  cursor: 'pointer',
                },
                '& .MuiDataGrid-row:hover': {
                  backgroundColor: 'action.hover',
                },
              }}
              onRowClick={(params) => {
                handleTemplateSelect(params.row);
              }}
              slotProps={{
                noRowsOverlay: {
                  title:
                    searchValue || appliedAdvancedFilterModel.conditions.length > 0
                      ? t('items.template.selectModal.noSearchResults')
                      : t('items.template.selectModal.noTemplates'),
                },
              }}
            />
          </Box>

          {/* Error Display */}
          {fieldError && (
            <Typography variant="body2" color="error">
              {fieldError.message}
            </Typography>
          )}
        </Box>

        {/* Right pane — preview / schema-detail tabs for the highlighted row.
            Hidden until a row is clicked so the table spans full width by default.
            Stretches to the full content height (matching the table on the left);
            the tab content area owns its own scroll, independent of the table. */}
        {previewTemplateId && (
          <Box
            sx={{
              flex: 1,
              minWidth: 0,
              minHeight: 0,
              display: 'flex',
              flexDirection: 'column',
              bgcolor: 'grey.50',
            }}
          >
            <Tabs
              value={previewTab}
              onChange={(_e, next: number) => {
                setPreviewTab(next);
              }}
              sx={{ borderBottom: 1, borderColor: 'divider', px: 1.5, flexShrink: 0 }}
            >
              <Tab label={t('items.template.selectModal.previewTab')} />
              <Tab label={t('items.template.selectModal.schemaTab')} />
            </Tabs>
            <Box sx={{ flex: 1, minHeight: 0, overflow: 'auto', p: 2.5 }}>
              {previewTab === 0 ? (
                <ItemTemplateDetail itemTemplateId={previewTemplateId} />
              ) : (
                <ItemTemplateSchemaDetail itemTemplateId={previewTemplateId} readOnly />
              )}
            </Box>
          </Box>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={handleCancel} disabled={disabled}>
          {t('common.cancel')}
        </Button>
        <Button
          onClick={handleSelect}
          variant="contained"
          disabled={disabled || selectedTemplates.length === 0}
        >
          {multiValue && selectedTemplates.length > 0
            ? `${t('common.select')} (${selectedTemplates.length})`
            : t('common.select')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
